import { HEALTHY_RATE_RANGE, type SessionMetricsTracker } from "./metrics.ts";
import type { BlinkEvent, SuppressionLevel } from "./types.ts";

/**
 * The pressure channel (game design doc §6).
 *
 * The design thesis is an inversion: **do not reward blinking, penalise
 * suppressing it**. A game that scores blinks produces someone blinking
 * artificially for three minutes and dropping to 5/min the moment they close
 * the app — the opposite of restoring a spontaneous reflex.
 *
 * That inversion cannot be expressed with discrete BlinkEvents alone. Rewarding
 * an event is trivial; penalising the *absence* of one needs a continuous
 * signal that grows while nothing happens. Hence: mechanics subscribe here for
 * pressure and to BlinkEvent for release. Continuous pressure, discrete release
 * — that split is the shape the whole design wants.
 */

/** A resting adult blinks every ~3–4s. Below that, nothing is wrong. */
const HEALTHY_INTERVAL_MS = 60_000 / HEALTHY_RATE_RANGE[0];

/** Where suppression reaches maximum. Three healthy intervals of nothing. */
const FULL_SUPPRESSION_MS = HEALTHY_INTERVAL_MS * 3;

/** 10Hz. Fixed, so game feel is decoupled from whatever the camera manages. */
const TICK_MS = 100;

/**
 * Smoothstep rather than a straight ramp.
 *
 * A linear rise makes the blur mechanic switch on at a detectable instant,
 * which invites the player to treat it as a threshold to game. Flat at both
 * ends means pressure creeps in unnoticed and eases off the same way — the
 * player should never be thinking about their eyes (design doc §7).
 */
const smoothstep = (t: number) => t * t * (3 - 2 * t);

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export class SuppressionTracker {
  #lastBlinkAt: number | null = null;
  #lastEmitAt = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly metrics: SessionMetricsTracker,
    /** Amplitude at which a blink counts as fully closing. */
    private readonly completeAmplitude = 0.7,
  ) {}

  /**
   * Releases pressure in proportion to how far the eyelid actually closed.
   *
   * Deliberately continuous, with no complete/partial branch — and that is a
   * correction to the first version of this file, which reset fully on
   * "complete" and gave partial credit otherwise.
   *
   * Replaying six real sessions through that version exposed the problem: one
   * session sat above half pressure for 22% of its length with a 12.3s
   * unbroken stretch, while a near-identical one never left zero. The
   * difference was not behaviour, it was classification. `completeAmplitude`
   * currently sits on the median of the observed amplitude distribution, so
   * whether a given blink lands in one bucket or the other is close to a coin
   * flip — and a binary release turns that coin flip into a cliff the whole
   * mechanic falls off.
   *
   * Scaling by amplitude keeps design doc §5 intact (a fuller blink genuinely
   * buys more relief, so the game still teaches closing properly) while making
   * a blink at 0.68 behave almost exactly like one at 0.72. The mechanic stops
   * depending on a threshold nobody has validated yet.
   */
  onBlink(event: BlinkEvent): void {
    if (this.#lastBlinkAt === null) {
      this.#lastBlinkAt = event.t;
      return;
    }
    const relief = clamp01(event.amplitude / this.completeAmplitude);
    this.#lastBlinkAt += (event.t - this.#lastBlinkAt) * relief;
  }

  /** @returns a level to emit, or null between ticks. */
  tick(t: number): SuppressionLevel | null {
    this.#lastBlinkAt ??= t;

    if (t - this.#lastEmitAt < TICK_MS) return null;
    // Advance by whole ticks rather than snapping to the frame that happened to
    // cross the deadline. Frame intervals rarely divide TICK_MS evenly — 67ms
    // frames against a 100ms tick — and snapping would push the effective rate
    // steadily lower instead of averaging out.
    this.#lastEmitAt =
      this.#lastEmitAt === Number.NEGATIVE_INFINITY
        ? t
        : this.#lastEmitAt + Math.floor((t - this.#lastEmitAt) / TICK_MS) * TICK_MS;

    const elapsed = t - this.#lastBlinkAt;
    const raw = clamp01(
      (elapsed - HEALTHY_INTERVAL_MS) / (FULL_SUPPRESSION_MS - HEALTHY_INTERVAL_MS),
    );

    const snapshot = this.metrics.snapshot();
    const total = snapshot.totalComplete + snapshot.totalPartial;

    return {
      type: "suppression",
      t,
      value: smoothstep(raw),
      msSinceLastBlink: elapsed,
      rollingRatePerMin: snapshot.ratePerMinute,
      completenessRatio: total > 0 ? snapshot.totalComplete / total : 1,
    };
  }

  reset(): void {
    this.#lastBlinkAt = null;
    this.#lastEmitAt = Number.NEGATIVE_INFINITY;
  }
}

export { FULL_SUPPRESSION_MS, HEALTHY_INTERVAL_MS };
